import {
  useEffect,
  useState,
  useMemo,
  memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  Divider,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  LinearProgress
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  ChevronRight as ChevronRightIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getTankList } from 'src/redux/tankSlice';
import Loader from 'src/common/Loader';
import ROUTE from 'src/utils/routeConstants';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, tankTableHeaders } from 'src/utils/tableHeaders';
import { getFuelQuantity, getBalanceValue, getSelectedSiteFilterValue } from 'src/utils/getInitials';
import CustomPagination from 'src/common/CustomPagination';
import clampMinZero from 'src/utils/numberUtils';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

const fuelSweep = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const getFuelLevelMeta = (percentage) => {
  const value = clampMinZero(Number(percentage) || 0);

  if (value <= 25) {
    return {
      value,
      label: 'Critical',
      textColor: '#FFFFFF',
      gradient: 'linear-gradient(90deg, rgba(211, 47, 47, 0.7), rgba(239, 83, 80, 0.7), rgba(211, 47, 47, 0.7))',
      barColor: '#d32f2f'
    };
  }

  if (value <= 50) {
    return {
      value,
      label: 'Low',
      textColor: '#1f2937',
      gradient: 'linear-gradient(90deg, rgba(251, 140, 0, 0.7), rgba(255, 183, 77, 0.7), rgba(251, 140, 0, 0.7))',
      barColor: '#fb8c00'
    };
  }

  if (value <= 75) {
    return {
      value,
      label: 'Medium',
      textColor: '#1f2937',
      gradient: 'linear-gradient(90deg, rgba(251, 192, 45, 0.7), rgba(255, 241, 118, 0.7), rgba(251, 192, 45, 0.7))',
      barColor: '#fbc02d'
    };
  }

  return {
    value,
    label: 'High',
    textColor: '#FFFFFF',
    gradient: 'linear-gradient(90deg, rgba(46, 125, 50, 0.7), rgba(102, 187, 106, 0.7), rgba(46, 125, 50, 0.7))',
    barColor: '#2e7d32'
  };
};

const getDispenserName = (tank) => tank?.dispenser_name
  || tank?.dispenser?.station_name
  || tank?.dispenser?.name
  || tank?.dispenser?.dispenser_name
  || '-';

const FuelLevelBadge = ({ percentage }) => {
  const {
    value,
    label,
    textColor,
    gradient
  } = getFuelLevelMeta(percentage);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.2,
        py: 0.45,
        borderRadius: 999,
        color: textColor,
        fontWeight: 700,
        fontSize: '0.78rem',
        letterSpacing: 0.2,
        backgroundImage: gradient,
        backgroundSize: '200% 100%',
        animation: `${fuelSweep} 2.8s linear infinite`,
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)'
      }}
    >
      <Box component="span">
        {value}
        %
      </Box>
      <Box component="span" sx={{ opacity: 0.92 }}>
        {label}
      </Box>
    </Box>
  );
};

FuelLevelBadge.propTypes = {
  percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const StatusPill = ({ enabled }) => (
  <Chip
    label={enabled ? 'Enabled' : 'Disabled'}
    color={enabled ? 'success' : 'error'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

StatusPill.propTypes = {
  enabled: PropTypes.bool
};

const MobileCardView = memo(({
  tank,
  onViewClick,
  onFuelGraphClick,
  theme
}) => {
  const balancePercentage = clampMinZero(getBalanceValue(tank));
  const fuelMeta = getFuelLevelMeta(balancePercentage);

  return (
    <Card
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography fontWeight={700}>
          {tank.name || 'N/A'}
        </Typography>
        <StatusPill enabled={tank.automation} />
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={1}>
        Type:
        {' '}
        {tank.type || 'N/A'}
      </Typography>

      <Typography variant="body2" mb={1}>
        Balance:
        {' '}
        {getFuelQuantity(
          clampMinZero(tank.current_balance?.stock),
          tank.current_balance?.stock_gallons
        )}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <FuelLevelBadge percentage={balancePercentage} />
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, balancePercentage)}
          sx={{
            height: 8,
            borderRadius: 5,
            bgcolor: alpha(theme.palette.common.black, 0.08),
            '& .MuiLinearProgress-bar': {
              backgroundColor: fuelMeta.barColor,
              transition: 'transform 700ms ease, background-color 700ms ease'
            }
          }}
        />
      </Box>

      <Stack direction="row" spacing={1}>
        {tank.fuel_graph_enabled && (
          <Tooltip title={tank.fuel_graph_mode === 'track_fuel' ? 'Track + Fuel' : 'Fuel Graph'}>
            <IconButton
              size="medium"
              aria-label="Fuel graph"
              sx={getTableActionIconButtonSx(theme)}
              onClick={() => onFuelGraphClick(tank.id)}
            >
              <ShowChartIcon sx={TABLE_ACTION_ICON_SX} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="medium"
          aria-label="View details"
          sx={getTableActionIconButtonSx(theme)}
          onClick={() => onViewClick(tank.id)}
        >
          <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
        </IconButton>
      </Stack>
    </Card>
  );
});

MobileCardView.propTypes = {
  tank: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    automation: PropTypes.bool,
    fuel_graph_enabled: PropTypes.bool,
    fuel_graph_mode: PropTypes.string,
    current_balance: PropTypes.shape({
      stock: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      stock_gallons: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  }).isRequired,
  onViewClick: PropTypes.func.isRequired,
  onFuelGraphClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired
};

MobileCardView.displayName = 'MobileCardView';

const TankListTable = ({
  refreshData, searchText, siteFilter, ...rest
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, tankList, totalCount } = useSelector((state) => state.tanks);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const tankAlignById = useMemo(() => getAlignByColumnId(tankTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(getTankList({ limit, pageNo: 1, params: { name: searchText, site: selectedSite } }));
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getTankList({ limit, pageNo: newPage + 1, params: { name: searchText, site: selectedSite } }));
  };

  const viewTankInfo = (tankId) => {
    history.push(`${ROUTE.tanks}/${tankId}`);
  };

  const viewTankFuelGraph = (tankId) => {
    history.push(`${ROUTE.tanks}/${tankId}?tab=fuel`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = tankList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ height: '100%' }}>
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {displayData.map((tank) => (
                <MobileCardView
                  key={tank.id}
                  tank={tank}
                  onViewClick={viewTankInfo}
                  onFuelGraphClick={viewTankFuelGraph}
                  theme={theme}
                />
              ))}
              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
              />
            </Box>
          ) : (
            <Box
              sx={{
                ...rest?.sx,
                width: '100%',
                borderRadius: 2,
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: 2
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: '#F9FAFB',
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: isTablet ? 800 : 1050 }}>
                  <Table stickyHeader>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={tankTableHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: '#F9FAFB',
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((tank) => {
                          const balancePercentage = clampMinZero(getBalanceValue(tank));
                          return (
                            <TableRow
                              hover
                              key={tank.id}
                              sx={{
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                '&:last-child': { borderBottom: 'none' },
                                '&:hover': { backgroundColor: '#F9FAFB' }
                              }}
                            >
                              <TableCell align={tankAlignById.name}>{tank.name}</TableCell>
                              <TableCell align={tankAlignById.type}>{tank.type}</TableCell>
                              <TableCell align={tankAlignById.dispenser_name}>
                                {getDispenserName(tank)}
                              </TableCell>
                              <TableCell align={tankAlignById.current_balance}>
                                {getFuelQuantity(
                                  clampMinZero(tank.current_balance?.stock),
                                  tank.current_balance?.stock_gallons
                                )}
                              </TableCell>
                              <TableCell align={tankAlignById.balance}>
                                <FuelLevelBadge percentage={balancePercentage} />
                              </TableCell>
                              <TableCell align={tankAlignById.automation}>
                                <StatusPill enabled={tank.automation} />
                              </TableCell>
                              <TableCell
                                align={tankAlignById.action}
                                sx={{
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 1,
                                  backgroundColor: '#FFFFFF',
                                  boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                                }}
                              >
                                {tank.fuel_graph_enabled && (
                                  <Tooltip title={tank.fuel_graph_mode === 'track_fuel' ? 'Track + Fuel' : 'Fuel Graph'}>
                                    <IconButton
                                      onClick={() => viewTankFuelGraph(tank.id)}
                                      size="medium"
                                      aria-label="Fuel graph"
                                      sx={{ ...getTableActionIconButtonSx(theme), mr: 0.75 }}
                                    >
                                      <ShowChartIcon sx={TABLE_ACTION_ICON_SX} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => viewTankInfo(tank.id)}
                                    size="medium"
                                    aria-label="View details"
                                    sx={getTableActionIconButtonSx(theme)}
                                  >
                                    <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={tankTableHeaders.length} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">
                              No Tanks Found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Divider sx={{ flexShrink: 0 }} />
              <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

TankListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default TankListTable;
